/**
 * Domain - Organizes the problem space (taxonomy).
 *
 * Domains form a hierarchical taxonomy that organizes Bounded Contexts.
 * Domains do NOT define meaning - they organize contexts that do.
 *
 * Core invariant: Domains organize; contexts define meaning.
 */
/**
 * Default implementation of Domain for runtime use.
 */
export class DomainImpl {
    constructor(params) {
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "uri", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "definition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_parent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_children", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "_boundedContexts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "_ontologies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        this.id = params.id;
        this.uri = params.uri;
        this.name = params.name;
        this.definition = params.definition;
        this._parent = params.parent;
    }
    getParent() {
        return this._parent;
    }
    getChildren() {
        return this._children;
    }
    getBoundedContexts() {
        return this._boundedContexts;
    }
    addBoundedContext(context) {
        // Check by ID to prevent duplicates (same context, different object instances)
        const existingIndex = this._boundedContexts.findIndex(c => c.id === context.id);
        if (existingIndex >= 0) {
            // Replace existing with new instance (e.g., Workspace replaces BoundedContextRef)
            this._boundedContexts[existingIndex] = context;
        }
        else {
            this._boundedContexts.push(context);
        }
    }
    addChild(child) {
        if (!this._children.includes(child)) {
            this._children.push(child);
        }
    }
    getOntologies() {
        return this._ontologies;
    }
    addOntology(ontology) {
        if (!this._ontologies.find(o => o.id === ontology.id)) {
            this._ontologies.push(ontology);
        }
    }
    /**
     * Serialize to JSON-LD with per-object @context.
     * Subdomains inherit @context from their parent, so they are serialized without it.
     *
     * @param options.includeContext - Whether to include @context (default: true for root, false for subdomains)
     */
    toJSON(options) {
        const includeContext = options?.includeContext ?? true;
        const base = {
            '@type': 'Domain',
            '@id': this.uri,
            name: this.name,
            ...(this.definition ? { definition: this.definition } : {}),
            ontologies: this._ontologies.map(ont => ({
                '@type': 'Ontology',
                '@id': ont.namespace ?? ont.id,
                label: ont.label
            })),
            contexts: this._boundedContexts.map(ctx => {
                // Use toJSON if available (Workspace), otherwise serialize minimally
                if ('toJSON' in ctx && typeof ctx.toJSON === 'function') {
                    return ctx.toJSON();
                }
                // Fallback for BoundedContextRef or minimal implementations
                return {
                    '@context': { '@vocab': 'http://semantical.io/ontology/core#' },
                    '@type': 'BoundedContext',
                    '@id': ctx.uri,
                    name: ctx.name,
                    ...(ctx.definition ? { definition: ctx.definition } : {})
                };
            }),
            // Subdomains inherit @context from parent, so serialize without it
            subdomains: this._children.map(child => {
                if ('toJSON' in child && typeof child.toJSON === 'function') {
                    return child.toJSON({ includeContext: false });
                }
                // Minimal serialization for domains without toJSON (no @context needed)
                return {
                    '@type': 'Domain',
                    '@id': child.uri,
                    name: child.name
                };
            })
        };
        if (includeContext) {
            return {
                '@context': {
                    '@vocab': 'http://semantical.io/ontology/core#',
                    ontologies: { '@id': 'hasOntology', '@type': '@id', '@container': '@set' },
                    contexts: { '@id': 'containsContext', '@type': '@id', '@container': '@set' },
                    subdomains: { '@id': 'hasSubdomain', '@type': '@id', '@container': '@set' }
                },
                ...base
            };
        }
        return base;
    }
}
// Note: Domain instances are now loaded from workspace-config.json
// via WorkspaceConfigLoader. The hardcoded MY_DOMAIN has been removed.
// Use loadInitialConfig() from WorkspaceConfigLoader to get root domains.
